import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

/**
 * Rota Protegida (Refinada para Real Benefícios)
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string[]} [props.allowedRoles] - Array de perfis permitidos (ex: ['admin', 'gerente', 'user'])
 */
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { currentUser, companyInfo, userInfo, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Evita o piscar de tela enquanto o Firebase Auth resolve o token
    return null;
  }

  // 1. BLINDAGEM DE SESSÃO: Se NÃO está logado, manda para o login guardando de onde veio
  if (!currentUser) {
    if (location.pathname !== "/login") {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return children;
  }

  // 2. BYPASS DE ELITE (Alinhado com o firestore.rules: hasGlobalAccess)
  if (userInfo?.role === "superadmin" || userInfo?.isInternal === true) {
    return children;
  }

  // 3. BLINDAGEM DE STATUS (Tenant ou Usuário Inativo/Bloqueado)
  const isCompanyBlocked = companyInfo && (companyInfo.active === false || companyInfo.status === "blocked" || companyInfo.status === "inactive");
  const isUserBlocked = userInfo && (userInfo.active === false || userInfo.status === "blocked" || userInfo.status === "inactive");

  if (isCompanyBlocked || isUserBlocked) {
    if (location.pathname !== "/conta-bloqueada") {
      return <Navigate to="/conta-bloqueada" replace />;
    }
    return children; // Se já está na página de bloqueio, deixa renderizar
  }

  // 4. Se a conta está normal e tenta acessar a página de bloqueio, devolve pro dashboard
  if (location.pathname === "/conta-bloqueada") {
    return <Navigate to="/dashboard" replace />;
  }

  // 5. Redirecionamento Base (Raiz para Dashboard)
  if (location.pathname === "/") {
    return <Navigate to="/dashboard" replace />;
  }

  // 6. BLINDAGEM DE SENHA: Força troca de senha
  if (userInfo?.forcePasswordChange && location.pathname !== "/auth/change-password") {
    return <Navigate to="/auth/change-password" replace />;
  }
  if (!userInfo?.forcePasswordChange && location.pathname === "/auth/change-password") {
    return <Navigate to="/dashboard" replace />;
  }

  // 7. 🚨 RBAC BLINDADO (Role Based Access Control - Fail Closed)
  if (allowedRoles && allowedRoles.length > 0) {
    // Se a role não existir no usuário ou não estiver na lista permitida, bloqueia!
    if (!userInfo?.role || !allowedRoles.includes(userInfo.role)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;